import React, { useMemo, useState } from "react";
import { useAdminGetListedAgencies } from "../../../api/hooks/admin";
import { Spinner } from "../../../components";

interface AgencyPickerProps {
  selectedAgencyId: string | null;
  onSelect: (agencyId: string | null, name: string | null) => void;
}

const AgencyPicker: React.FC<AgencyPickerProps> = ({
  selectedAgencyId,
  onSelect,
}) => {
  const [search, setSearch] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const { data: agencies, isLoading } = useAdminGetListedAgencies(showInactive);

  const filtered = useMemo(() => {
    if (!agencies) return [];
    const s = search.trim().toLowerCase();
    return agencies
      .filter((a) => !s || (a.name || "").toLowerCase().includes(s))
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [agencies, search]);

  const selected = agencies?.find((a) => a.agencyId === selectedAgencyId) ?? null;

  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 8,
        padding: 16,
        width: "100%",
        maxWidth: 1200,
        boxSizing: "border-box",
      }}
    >
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center", marginBottom: 12 }}>
        <div style={{ fontWeight: 600, fontSize: 16 }}>Choose an agency</div>
        {selected ? (
          <div
            style={{
              padding: "4px 10px",
              borderRadius: 999,
              background: "#eef2ff",
              color: "#3949ab",
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            {selected.name}
          </div>
        ) : null}
        <input
          type="text"
          placeholder="Search agencies"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            marginLeft: "auto",
            padding: "8px 12px",
            border: "1px solid #d1d5db",
            borderRadius: 6,
            fontSize: 13,
            minWidth: 200,
          }}
        />
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#374151" }}>
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
          />
          Include inactive
        </label>
      </div>

      {isLoading ? (
        <Spinner />
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
            gap: 8,
            maxHeight: 280,
            overflowY: "auto",
            border: "1px solid #f3f4f6",
            borderRadius: 6,
            padding: 8,
          }}
        >
          {filtered.map((a) => {
            const isSelected = a.agencyId === selectedAgencyId;
            return (
              <button
                key={a.agencyId}
                onClick={() => onSelect(a.agencyId, a.name)}
                style={{
                  textAlign: "left",
                  padding: "8px 10px",
                  border: isSelected ? "1px solid #3949ab" : "1px solid #e5e7eb",
                  borderRadius: 6,
                  background: isSelected ? "#eef2ff" : "#fff",
                  cursor: "pointer",
                  color: a.isListed ? "#111827" : "#9ca3af",
                  fontSize: 13,
                }}
              >
                <div style={{ fontWeight: 600 }}>{a.name}</div>
                <div style={{ fontSize: 11, color: "#6b7280" }}>{a.agencyId}</div>
              </button>
            );
          })}
          {filtered.length === 0 ? (
            <div style={{ color: "#6b7280", padding: 12, fontSize: 13 }}>No agencies match.</div>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default AgencyPicker;
